import React from 'react';
import yaml from 'js-yaml';
// renderers is imported separately so Markdoc isn't sent to the client
import Markdoc, {renderers} from '@markdoc/markdoc'

import {getSchema, defaultObject} from '/vercel/path0/node_modules/@markdoc/next.js/src/runtime.js';
/**
 * Schema is imported like this so end-user's code is compiled using build-in babel/webpack configs.
 * This enables typescript/ESnext support
 */
const config = {};
import * as tags from '/vercel/path0/markdoc/tags.js'
import * as nodes from '/vercel/path0/markdoc/nodes/index.js'
const functions = {};
const schema = {
tags: defaultObject(tags),
nodes: defaultObject(nodes),
functions: defaultObject(functions),
...defaultObject(config),
};

const tokenizer = new Markdoc.Tokenizer();

/**
 * Source will never change at runtime, so parse happens at the file root
 */
const source = "---\ndescription: Documentation of the geOps Realtime API. Get realtime vehicle positions and their journey worlwide.\nicon: train\ntitle: Realtime\nsubtitle: Realtime vehicle positions\nswagger: /swagger/realtime.json\nweight: 3\n---\n\nRealtime vehicle positions and stop sequences based on scheduled times and\nrealtime updates.\n\nThe API is reachable at `wss://api.geops.io/tracker-ws/v1/?key=<key>` where\n`<key>` is your API key.\n\nWe also provide a [client library](https://mobility-toolbox-js.geops.io/example/ol-tracker) using this API to display vehicles in a map.\n\nTo provide realtime updates we implemented a Websocket protocol using simple\ntext commands to request data from the backend, which is delivered in (Geo)JSON.\n\n[`EPSG:3857` \"Web Mercator\"](https://en.wikipedia.org/wiki/Web_Mercator_projection#EPSG:3857) is used for all coordinates, including in GeoJSON\nobjects here.\n\n## Websocket API\n\nThis API is based on our [redis-websocket-api](https://github.com/geops/redis-websocket-api).\n\nEach response frame is wrapped in an envelope:\n\n```json\n{\n    \"source\":\"<channel_name>|trajectory\",\n    \"timestamp\":<unix_timestamp_in_ms>,\n    \"client_reference\":\"None\",\n    \"content\": {...}\n}\n```\n\n### Vehicle Positions\n\nThe `\"source\"` is set to `\"trajectory\"` for vehicle positions.\n\nTo receive vehicle positions you need to specify a bounding box including a\nzoom level, optionally filtered by modes of transport, generalization, or data\nsource (called tenant here):\n\n```\nBBOX <left> <bottom> <right> <top> <zoom>[ tenant=<tenant>][ gen=<generalization>][ mots=<mot1,mot2,...>]\n```\n\n- `left bottom right top` describe the extent of the bounding box in epsg:3857 coordinates\n- `zoom` is the zoom level of the base map; lower means less detailed geometries and MOTs\n- `mots` are the modes of transport, decided based on the zoom by default. Choices are:\n  - `tram`\n  - `subway`\n  - `rail`\n  - `bus`\n  - `ferry`\n  - `cablecar`\n  - `gondola`\n  - `funicular`\n  - `coach`\n- `gen` is relevant when using our public transport maps where we generalize\n  based on the extent and zoom of the viewport. Choices are:\n\n  - `5`\n  - `10`\n  - `30`\n  - `100`\n\n  Other values are also supported but fall back to one of the levels above or\n  no generalization for out of range values.\n\n- `tenant` can be used to limit results to one of the data sources\n\n### Trip Details\n\nOther data requests use the `geops/redis-websocket-api` protocol, short summery\nof the commands:\n\n```\nGET <key> // fetches all data from a channel\nGET <key> <id> // fetches a specific item from the channel\nSUB <key> // subscribes the websocket to a channel to get event-based updates\nDEL <key> // unsubscribes the client from the channel\nPING // causes a PONG response (to avoid timeouts or validate the connection)\n```\n\n#### Full trajectory\n\nRequest the geometry for the complete trajectory by performing a\n\n```\nGET full_trajectory_<train_id>\n```\n\nwhere the `train_id` is taken from the vehicle position data.\n\n#### Stop sequence\n\nThe stop sequence can be requested using\n\n```\nGET stopsequence_<train_id>\n```\n\nwhere the `train_id` is taken from the vehicle position data.\n";
const filepath = "/apis/realtime.md";
const tokens = tokenizer.tokenize(source);
const ast = Markdoc.parse(tokens);

/**
 * Like the AST, frontmatter won't change at runtime, so it is loaded at file root.
 * This unblocks future features, such a per-page dataFetchingFunction.
 */
const frontmatter = ast.attributes.frontmatter
  ? yaml.load(ast.attributes.frontmatter)
  : {};

const {components, ...rest} = getSchema(schema)

export async function getStaticProps(context) {
  const partials = {};

  // Ensure Node.transformChildren is available
  Object.keys(partials).forEach((key) => {
    const tokens = tokenizer.tokenize(partials[key]);
    partials[key] = Markdoc.parse(tokens);
  });

  const cfg = {
    ...rest,
    variables: {
      ...(rest ? rest.variables : {}),
      // user can't override this namespace
      markdoc: {frontmatter},
      // Allows users to eject from Markdoc rendering and pass in dynamic variables via getServerSideProps
      ...(context.variables || {})
    },
    partials,
    source,
  };

  /**
   * transform must be called in dataFetchingFunction to support server-side rendering while
   * accessing variables on the server
   */
  const content = await Markdoc.transform(ast, cfg);

  return {
    // Removes undefined
    props: JSON.parse(
      JSON.stringify({
        markdoc: {
          content,
          frontmatter,
          file: {
            path: filepath
          }
        },
      })
    ),
  };
}

export default function MarkdocComponent(props) {
  // Only execute HMR code in development
  return renderers.react(props.markdoc.content, React, {
    components: {
      ...components,
      // Allows users to override default components at runtime, via their _app
      ...props.components,
    },
  });
}
